<template>
  <v-dialog v-model="isOpen" max-width="800" scrollable>
    <v-card>
      <!-- Header -->
      <v-card-title class="d-flex align-center pa-4 bg-green-lighten-5">
        <v-icon color="green-darken-2" class="mr-2">mdi-map-marker-radius</v-icon>
        <span class="text-h6">Contacten in de buurt</span>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="close"
        ></v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Content -->
      <v-card-text class="pa-0">
        <!-- Loading state -->
        <div
          v-if="loading && contacts.length === 0"
          class="d-flex justify-center align-center py-8"
        >
          <v-progress-circular
            indeterminate
            color="green-darken-2"
            size="48"
          ></v-progress-circular>
        </div>

        <!-- Error state -->
        <div v-else-if="error && contacts.length === 0" class="pa-6 text-center">
          <v-icon size="48" color="grey-lighten-1" class="mb-3">
            mdi-alert-circle-outline
          </v-icon>
          <div class="text-body-1 text-medium-emphasis">{{ error }}</div>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="contacts.length === 0"
          class="pa-6 text-center"
        >
          <v-icon size="48" color="grey-lighten-1" class="mb-3">
            mdi-map-marker-off
          </v-icon>
          <div class="text-h6 mb-2">Geen contacten gevonden</div>
          <div class="text-body-2 text-medium-emphasis">
            Er zijn geen contacten met een dekkingsgebied dat deze locatie
            omvat.
          </div>
        </div>

        <!-- Contacts list -->
        <v-list v-else class="py-0">
          <v-list-item
            v-for="contact in contacts"
            :key="contact.id"
            class="contact-item"
            @click="navigateToContact(contact.id)"
          >
            <template #prepend>
              <v-avatar
                :color="getContactColor(contact.type)"
                class="mr-3"
              >
                <v-icon color="white">{{ getContactIcon(contact.type) }}</v-icon>
              </v-avatar>
            </template>

            <v-list-item-title class="font-weight-medium">
              {{ contact.name }}
            </v-list-item-title>

            <v-list-item-subtitle v-if="contact.parent_name" class="mb-1">
              <v-icon size="x-small" class="mr-1">mdi-sitemap</v-icon>
              {{ contact.parent_name }}
            </v-list-item-subtitle>

            <v-list-item-subtitle class="contact-details">
              <!-- Phone -->
              <span v-if="contact.primary_phone_formatted" class="detail-item">
                <v-icon size="small" class="mr-1">mdi-phone</v-icon>
                {{ contact.primary_phone_formatted }}
                <span v-if="contact.primary_phone_type" class="text-caption">
                  ({{ contact.primary_phone_type }})
                </span>
              </span>

              <!-- Email -->
              <span v-if="contact.primary_email_address" class="detail-item">
                <v-icon size="small" class="mr-1">mdi-email</v-icon>
                {{ contact.primary_email_address }}
              </span>

              <!-- Address -->
              <span v-if="contact.primary_location_address" class="detail-item">
                <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                {{ contact.primary_location_address }}
              </span>
            </v-list-item-subtitle>

            <template #append>
              <v-btn
                icon="mdi-chevron-right"
                variant="text"
                size="small"
                color="grey"
              ></v-btn>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-divider v-if="contacts.length > 0"></v-divider>

      <!-- Footer -->
      <v-card-actions v-if="contacts.length > 0" class="pa-3">
        <div class="text-caption text-medium-emphasis">
          {{ contacts.length }} contact{{ contacts.length !== 1 ? 'en' : '' }}
          gevonden
        </div>
        <v-spacer></v-spacer>
        <v-btn color="green-darken-2" variant="text" @click="close">
          Sluiten
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  contacts: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);
const router = useRouter();

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const close = () => {
  isOpen.value = false;
};

const navigateToContact = (contactId) => {
  router.push(`/contacts/${contactId}`);
  close();
};

const getContactIcon = (type) => {
  if (!type) return 'mdi-account';

  const typeStr = String(type).toLowerCase();

  if (typeStr.includes('organization')) return 'mdi-domain';
  if (typeStr.includes('department')) return 'mdi-office-building';
  if (typeStr.includes('individual')) return 'mdi-account';

  return 'mdi-account';
};

const getContactColor = (type) => {
  if (!type) return 'grey';

  const typeStr = String(type).toLowerCase();

  if (typeStr.includes('organization')) return 'blue';
  if (typeStr.includes('department')) return 'teal';
  if (typeStr.includes('individual')) return 'green';

  return 'grey';
};
</script>

<style scoped>
.contact-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.contact-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
}

.detail-item .v-icon {
  color: rgba(0, 0, 0, 0.54);
}
</style>

